import buildAppDiffs from '@/lib/buildAppDiffs.js';
import { createSelector } from '@/redux/lib/createSelector.js';
import { issues } from '@/redux/selectors/issueSelectors';
import { query } from '@/redux/selectors/querySelectors';
import { param } from '@/redux/selectors/uiSelectors';
import { admin, companyId } from '@/redux/selectors/userSelectors';
import { appActionOutboundOutputs, appActionOutputs, appsMetadata, credentials } from '@/redux/selectors/workflowSelectors';
import findComparableAppVersion from '@/screens/apps/lib/findComparableAppVersion.js';
import { agentAppBuildingSkillTree } from '@shared/schema/agentAppBuildingSkillTree.js';
import { validateActionAttributesGroup, validateActionOutboundOutputGroup, validateActionOutputGroup, validateActionPresentationGroup, validateActionSignatureGroup } from '@/screens/apps/lib/appSchemaValidator';

const stableArray = [];
const agentActionsSkillTreeTotal = agentAppBuildingSkillTree?.actions?.length ?? 0;

export const appsVersions = (state) => state?.workflow?.appsVersions;
export const activeApp = createSelector(
  'activeApp',
  [(state) => param(state, 'appId'), (state) => param(state, 'version'), appsVersions], //
  (appId, appVersion, apps) => apps?.find((v) => v?.id === appId && String(v.version) === String(appVersion))
);
export const activeAppId = createSelector('activeAppId', [activeApp], (app) => app?.id);
export const activeAppMetadata = createSelector(
  'activeAppMetadata',
  [(state) => param(state, 'appId'), appsMetadata], //
  (appId, apps) => apps?.find((v) => v?.id === appId)
);

export const activeAppAction = createSelector(
  'activeAppAction',
  [(state) => param(state, 'arg1'), (state) => query(state, 'actionId'), activeApp], //
  (arg1, actionId, app) => app?.actions?.find((a) => a.id === actionId || a.id === arg1)
);
export const activeAppActionAgentSkillTree = createSelector('activeAppActionAgentSkillTree', [activeAppAction], (action) => action?.meta?.agentSkillTree ?? []);
export const activeAppActionAgentSkillTreePercentage = createSelector('activeAppActionAgentSkillTreePercentage', [activeAppActionAgentSkillTree], (tree) => {
  const total = agentActionsSkillTreeTotal;
  const completed = tree.filter((r) => r?.success === true).length;
  return total > 0 ? Math.round((completed / total) * 100) : undefined;
});
export const activeAppActionById = createSelector(
  'activeAppActionById',
  activeApp,
  (_, actionId) => actionId,
  (version, actionId) => version?.actions?.find((a) => a.id === actionId)
);
export const activeAppActionArgument = createSelector(
  'activeAppActionArgument',
  [(state) => query(state, 'argumentId'), activeAppAction], //
  (id, action) => action?.arguments?.find((a) => a.id === id)
);
export const activeAppActionArgumentChildren = createSelector('activeAppActionArgumentChildren', [activeAppActionArgument, activeAppAction], (parent, action) => action?.arguments?.filter((a) => a.key?.startsWith(parent?.key) && a.key !== parent?.key));
export const activeAppActionUsesRawBody = createSelector('activeAppActionUsesRawBody', [activeAppAction], (action) => action?.arguments?.some((a) => a.useRawBody === true));
export const activeAppActionInstance = createSelector('activeAppActionInstance', [activeAppAction], (action) => action?.instance);
export const activeAppActionLongPollingInstance = createSelector('activeAppActionLongPollingInstance', [activeAppAction], (action) => action?.longPolling?.actionInstance);
export const activeAppExtraActionLongPollingInstance = createSelector('activeAppExtraActionLongPollingInstance', [activeAppAction], (action) => action?.longPolling?.extraAction?.instance);
export const activeAppActionInstanceById = createSelector('activeAppActionInstanceById', [activeAppActionById], (action) => action?.instance);
export const activeAppActionInstanceArguments = createSelector('activeAppActionInstanceArguments', [activeAppActionInstance], (instance) => instance?.arguments);
export const activeAppExtraActionLongPollingInstanceArguments = createSelector('activeAppExtraActionLongPollingInstanceArguments', [activeAppExtraActionLongPollingInstance], (instance) => instance?.arguments);
export const activeAppActionLongPollingInstanceArguments = createSelector('activeAppActionLongPollingInstanceArguments', [activeAppActionLongPollingInstance], (instance) => instance?.arguments);
export const activeAppActionInstanceArgumentsById = createSelector('activeAppActionInstanceArgumentsById', [activeAppActionInstanceById], (instance) => instance?.arguments);
export const activeAppActionInstanceOptions = createSelector('activeAppActionInstanceOptions', [activeAppActionInstance], (instance) => instance?.options);
export const activeAppExtraActionLongPollingInstanceOptions = createSelector('activeAppExtraActionLongPollingInstanceOptions', [activeAppExtraActionLongPollingInstance], (instance) => instance?.options);
export const activeAppActionLongPollingInstanceOptions = createSelector('activeAppActionLongPollingInstanceOptions', [activeAppActionLongPollingInstance], (instance) => instance?.options);
export const activeAppActionInstanceArgumentByKeyAndIndex = createSelector(
  'activeAppActionInstanceArgumentByKeyAndIndex',
  activeAppActionInstanceArguments,
  (_, key, __) => key,
  (_, __, index) => index,
  (args, key, index) => (index !== undefined ? args?.[key]?.[index] : args?.[key])
);
export const activeAppActionLongPollingInstanceArgumentByKeyAndIndex = createSelector(
  'activeAppActionLongPollingInstanceArgumentByKeyAndIndex',
  activeAppActionLongPollingInstanceArguments,
  (_, key, __) => key,
  (_, __, index) => index,
  (args, key, index) => (index !== undefined ? args?.[key]?.[index] : args?.[key])
);
export const activeAppExtraActionLongPollingInstanceArgumentByKeyAndIndex = createSelector(
  'activeAppExtraActionLongPollingInstanceArgumentByKeyAndIndex',
  activeAppExtraActionLongPollingInstanceArguments,
  (_, key, __) => key,
  (_, __, index) => index,
  (args, key, index) => (index !== undefined ? args?.[key]?.[index] : args?.[key])
);
export const activeAppActionListInstanceOptions = createSelector('activeAppActionListInstanceOptions', [activeAppActionArgument], (argument) => argument?.list?.actionInstance?.options);
export const activeAppActionListTestInstanceOptions = createSelector('activeAppActionListTestInstanceOptions', [activeAppActionArgument], (argument) => argument?.list?.testInstance?.options);
export const activeAppActionListInstanceArguments = createSelector('activeAppActionListInstanceArguments', [activeAppActionArgument], (argument) => argument?.list?.actionInstance?.arguments);
export const activeAppActionListTestInstanceArguments = createSelector('activeAppActionListTestInstanceArguments', [activeAppActionArgument], (argument) => argument?.list?.testInstance?.arguments);
export const activeAppActionListInstanceArgumentByKeyAndIndex = createSelector(
  'activeAppActionListInstanceArgumentByKeyAndIndex',
  activeAppActionArgument,
  (_, key, __) => key,
  (_, __, index) => index,
  (argument, key, index) => {
    const listArguments = argument?.list?.actionInstance?.arguments;
    return index !== undefined ? listArguments?.[key]?.[index] : listArguments?.[key];
  }
);
export const activeAppActionListTestInstanceArgumentByKeyAndIndex = createSelector(
  'activeAppActionListTestInstanceArgumentByKeyAndIndex',
  activeAppActionArgument,
  (_, key, __) => key,
  (_, __, index) => index,
  (argument, key, index) => {
    const listArguments = argument?.list?.testInstance?.arguments;
    return index !== undefined ? listArguments?.[key]?.[index] : listArguments?.[key];
  }
);
export const activeAppActionFeatures = createSelector('activeAppActionFeatures', [activeAppAction], (action) => action?.features);
export const activeAppActionFeaturesCustomArguments = createSelector('activeAppActionFeaturesCustomArguments', [activeAppAction], (action) => action?.features?.customArguments);
export const activeAppActionFeaturesCustomArgumentsByKeyAndIndex = createSelector(
  'activeAppActionFeaturesCustomArgumentsByKeyAndIndex',
  activeAppActionFeaturesCustomArguments,
  (_, key, __) => key,
  (_, __, index) => index,
  (customArgs, key, index) => {
    const args = customArgs?.actionInstance?.arguments;
    return index !== undefined ? args?.[key]?.[index] : args?.[key];
  }
);
export const activeAppActionFeaturesFilters = createSelector('activeAppActionFeaturesFilters', [activeAppAction], (action) => action?.features?.filters);
export const activeAppActionFeaturesFiltersInstance = createSelector('activeAppActionFeaturesFiltersInstance', [activeAppActionFeaturesFilters], (filters) => filters?.actionInstance);
export const activeAppActionFeaturesFiltersInstanceOptions = createSelector('activeAppActionFeaturesFiltersInstanceOptions', [activeAppActionFeaturesFiltersInstance], (instance) => instance?.options);
export const activeAppActionFeaturesFiltersInstanceArguments = createSelector('activeAppActionFeaturesFiltersInstanceArguments', [activeAppActionFeaturesFiltersInstance], (instance) => instance?.arguments);
export const activeAppActionFeaturesFiltersInstanceArgumentByKeyAndIndex = createSelector(
  'activeAppActionFeaturesFiltersInstanceArgumentByKeyAndIndex',
  activeAppActionFeaturesFiltersInstanceArguments,
  (_, key, __) => key,
  (_, __, index) => index,
  (args, key, index) => (index !== undefined ? args?.[key]?.[index] : args?.[key])
);

export const activeAppActionFeaturesSort = createSelector('activeAppActionFeaturesSort', [activeAppAction], (action) => action?.features?.sort);
export const activeAppActionFeaturesSortInstance = createSelector('activeAppActionFeaturesSortInstance', [activeAppActionFeaturesSort], (sort) => sort?.actionInstance);
export const activeAppActionFeaturesSortInstanceOptions = createSelector('activeAppActionFeaturesSortInstanceOptions', [activeAppActionFeaturesSortInstance], (instance) => instance?.options);
export const activeAppActionFeaturesSortInstanceArguments = createSelector('activeAppActionFeaturesSortInstanceArguments', [activeAppActionFeaturesSortInstance], (instance) => instance?.arguments);
export const activeAppActionFeaturesSortInstanceArgumentByKeyAndIndex = createSelector(
  'activeAppActionFeaturesSortInstanceArgumentByKeyAndIndex',
  activeAppActionFeaturesSortInstanceArguments,
  (_, key, __) => key,
  (_, __, index) => index,
  (args, key, index) => (index !== undefined ? args?.[key]?.[index] : args?.[key])
);

export const activeAppActionOnCompletionHookInstance = createSelector('activeAppActionOnCompletionHookInstance', [activeAppAction], (action) => action?.hooks?.onCompletion?.actionInstance);
export const activeAppActionOnCompletionHookInstanceArguments = createSelector('activeAppActionOnCompletionHookInstanceArguments', [activeAppActionOnCompletionHookInstance], (instance) => instance?.arguments);
export const activeAppActionOnCompletionHookInstanceOptions = createSelector('activeAppActionOnCompletionHookInstanceOptions', [activeAppActionOnCompletionHookInstance], (instance) => instance?.options);
export const activeAppActionOnCompletionHookInstanceArgumentByKeyAndIndex = createSelector(
  'activeAppActionOnCompletionHookInstanceArgumentByKeyAndIndex',
  activeAppActionOnCompletionHookInstanceArguments,
  (_, key, __) => key,
  (_, __, index) => index,
  (args, key, index) => (index !== undefined ? args?.[key]?.[index] : args?.[key])
);

export const activeAppAllAuths = createSelector('activeAppAllAuths', activeApp, (version) => version?.authentication);
export const activeAppAuth = createSelector(
  'activeAppAuth',
  [(state) => param(state, 'arg1'), (state) => query(state, 'authId'), activeAppAllAuths], //
  (arg1, authId, auths) => auths?.find((a) => a?.id === authId || a?.id === arg1)
);
export const activeAppDefaultAuth = createSelector('activeAppDefaultAuth', activeApp, (version) => version?.authentication?.find((n) => n?.id === version?.defaultAuthId) || version?.authentication?.[0]);
export const activeAppAuthArguments = createSelector('activeAppAuthArguments', activeAppAuth, (auth) => (Array.isArray(auth?.arguments) ? auth.arguments : []));
export const activeAppAuthArgument = createSelector(
  'activeAppAuthArgument',
  [(state) => query(state, 'argumentId'), activeAppAuthArguments], //
  (id, args) => args?.find((a) => a.id === id)
);
export const activeAppDefaultTestCred = createSelector('activeAppDefaultTestCred', [activeAppDefaultAuth, credentials, activeApp], (defaultAuth, creds, activeApp) => {
  // First, check if there's an activeAppsScreenAuthId and a credential set for it
  const activeAppsScreenAuthId = activeApp?.activeAppsScreenAuthId;
  if (activeAppsScreenAuthId && activeApp?.authTypeCredentials?.[activeAppsScreenAuthId]) {
    const authTypeCredentialId = activeApp.authTypeCredentials[activeAppsScreenAuthId];
    const authTypeCred = creds.find((n) => n?.credentialId === authTypeCredentialId);
    if (authTypeCred) {
      return authTypeCred;
    }
  }

  // Fall back to defaultCredential
  const appDefaultCred = creds.find((n) => n?.companyId === activeApp?.defaultCredential?.companyId && n?.credentialId === activeApp?.defaultCredential?.credentialId);
  const defaultCred = creds.find((n) => n?.meta?.type === defaultAuth?.type && n?.appId === activeApp?.id && n?.isDefault);
  return appDefaultCred?.credentialId ? appDefaultCred : defaultCred;
});
export const activeAppUseAdminCredentials = createSelector('activeAppUseAdminCredentials', [activeAppDefaultTestCred], (cred) => cred?.companyId === 'MINDCLOUD');
export const activeAppAuthTestCreds = createSelector(
  'activeAppAuthTestCreds',
  [credentials, activeAppAuth, activeApp, (state) => param(state, 'appId'), admin, companyId], //
  (creds, auth, activeApp, appId, isAdmin, userCompanyId) => {
    if (!creds || !appId) return [];

    const effectiveCompanyId = isAdmin ? 'MINDCLOUD' : userCompanyId;

    let allCredentials = creds.filter((n) => n.companyId === effectiveCompanyId && n?.appId === appId && auth?.type && (n?.meta?.type === auth?.type || n?.secrets?.type === auth?.type));
    if (allCredentials.length === 0) {
      allCredentials = creds.filter((n) => n.companyId === effectiveCompanyId && n?.appId === appId);
    }

    const sortedCredentials = allCredentials.sort((a, b) => {
      if (a.isDefault !== b.isDefault) {
        return b.isDefault - a.isDefault; // isDefault first
      }
      return new Date(b?.updatedOn || 0) - new Date(a?.updatedOn || 0);
    });

    const appDefaultCred = creds.find((n) => n?.companyId === activeApp?.defaultCredential?.companyId && n?.credentialId === activeApp?.defaultCredential?.credentialId);
    if (appDefaultCred?.credentialId) {
      return [{ ...appDefaultCred, isDefault: true }, ...sortedCredentials?.map((n) => ({ ...n, isDefault: false }))];
    }

    return sortedCredentials;
  }
);
export const activeAppLatestTestCred = createSelector(
  'activeAppLatestTestCred',
  [credentials, activeAppAuth, (state) => param(state, 'appId'), admin, companyId], //
  (creds, auth, appId, isAdmin, userCompanyId) => {
    const effectiveCompanyId = isAdmin ? 'MINDCLOUD' : userCompanyId;
    return creds?.filter((n) => n.companyId === effectiveCompanyId && n?.meta?.type === auth?.type && n?.appId === appId)?.sort((a, b) => new Date(b?.updatedOn) - new Date(a?.updatedOn))?.[0];
  }
);
export const activeAppOAuth2 = createSelector('activeAppOAuth2', [activeApp], (app) => {
  const defaultAuthId = app?.defaultAuthId;
  const auth = app?.authentication?.find((n) => n.id === defaultAuthId);
  return auth?.type === 'oAuth2' ? auth : null;
});

export const activeAppApi = createSelector(
  'activeAppApi',
  [(state) => param(state, 'arg1'), (state) => query(state, 'apiId'), activeApp], //
  (arg1, apiId, version) => version?.api?.find((a) => a?.id === apiId || a?.id === arg1)
);
export const activeAppApis = createSelector('activeAppApis', [activeApp], (app) => app?.api || []);
export const activeAppApiById = createSelector(
  'activeAppApiById',
  activeApp,
  (_, apiId) => apiId,
  (app, apiId) => {
    if (!apiId) return null;
    return app?.api?.find((a) => a?.id === apiId);
  }
);
export const activeAppApiArgument = createSelector(
  'activeAppApiArgument',
  [(state) => param(state, 'arg4'), activeAppApi], //
  (id, api) => api?.arguments?.find((a) => a.id === id)
);
export const activeAppTriggerPayload = createSelector('activeAppTriggerPayload', [activeAppAction, appActionOutputs], (action, outputs) => (action?.type === 'trigger' ? outputs?.find((n) => n?.actionId === 'trigger') : undefined));
export const activeAppActionOutputs = createSelector('activeAppActionOutputs', [activeAppAction, appActionOutputs], (action, outputs) => outputs?.filter((n) => n?.actionId === action?.id));
export const activeAppAction200Output = createSelector('activeAppAction200Output', [activeAppActionOutputs], (outputs) => outputs?.find((n) => !n.statusId || (parseFloat(n.statusId) >= 200 && parseFloat(n.statusId) <= 299)));
export const activeAppAction400Output = createSelector('activeAppAction400Output', [activeAppActionOutputs], (outputs) => outputs?.find((n) => parseFloat(n.statusId) >= 400 && parseFloat(n.statusId) <= 499));
export const activeAppAction500Output = createSelector('activeAppAction500Output', [activeAppActionOutputs], (outputs) => outputs?.find((n) => parseFloat(n.statusId) >= 500 && parseFloat(n.statusId) <= 599));
export const activeAppActionResponse = createSelector('activeAppActionResponse', [activeAppAction, activeAppTriggerPayload, appActionOutputs], (action, triggerOutput, outputs) => outputs?.find((n) => n?.actionId === action?.id) || triggerOutput);
export const activeAppActionOutboundResponse = createSelector('activeAppActionOutboundResponse', [activeAppAction, activeAppTriggerPayload, appActionOutboundOutputs], (action, triggerOutput, outputs) => outputs?.find((n) => n?.actionId === action?.id) || triggerOutput);
export const activeAppActionAPI = createSelector('activeAppActionAPI', [activeAppAction, activeApp], (action, version) => {
  if (action?.api?.id) {
    return version?.api?.find((a) => a?.id === action?.api?.id);
  }
  return version?.api?.find((a) => a?.type === action?.api?.type);
});

// API Mappers
export const activeAppMappers = createSelector('activeAppMappers', [activeAppApi], (api) => (Array.isArray(api?.mappers) ? api.mappers : stableArray));
export const activeAppTriggerMappers = createSelector('activeAppTriggerMappers', [activeAppApi], (api) => (Array.isArray(api?.triggerMappers) ? api.triggerMappers : stableArray));
export const activeAppOutboundMappers = createSelector('activeAppOutboundMappers', [activeAppApi], (api) => (Array.isArray(api?.outboundMappers) ? api.outboundMappers : stableArray));

// API Mappers for Active Action
export const activeAppActionApiMappers = createSelector('activeAppActionApiMappers', [activeAppActionAPI], (api) => (Array.isArray(api?.mappers) ? api.mappers : stableArray));
export const activeAppActionApiTriggerMappers = createSelector('activeAppActionApiTriggerMappers', [activeAppActionAPI], (api) => (Array.isArray(api?.triggerMappers) ? api.triggerMappers : stableArray));
export const activeAppActionApiOutboundMappers = createSelector('activeAppActionApiOutboundMappers', [activeAppActionAPI], (api) => (Array.isArray(api?.outboundMappers) ? api.outboundMappers : stableArray));

// Action Outbound Mappers
export const activeAppActionOutboundMappers = createSelector('activeAppActionOutboundMappers', [activeAppAction], (action) => (Array.isArray(action?.outboundMappers) ? action.outboundMappers : stableArray));

export const activeAppActionAttribute = createSelector(
  'activeAppActionAttribute',
  [(state) => query(state, 'attributeId'), activeAppAction], //
  (id, action) => action?.attributes?.find?.((a) => a.id === id)
);
export const actionSignatureGroupValidation = createSelector('actionSignatureGroupValidation', [activeAppAction], validateActionSignatureGroup);
export const actionAttributesGroupValidation = createSelector('actionAttributesGroupValidation', [activeAppAction], validateActionAttributesGroup);
export const actionOutputGroupValidation = createSelector('actionOutputGroupValidation', [activeAppActionResponse], validateActionOutputGroup);
export const actionOutboundOutputGroupValidation = createSelector('actionOutboundOutputGroupValidation', [activeAppActionOutboundResponse], validateActionOutboundOutputGroup);
export const actionPresentationGroupValidation = createSelector('actionPresentationGroupValidation', [activeAppAction], validateActionPresentationGroup);
export const appDiffs = createSelector('appDiffs', [appsVersions, activeAppMetadata, activeApp], (apps, appMetadata, app) => {
  const lastPublishedVersion = appMetadata?.publishedVersion || 'draft';
  const lastPublishedObject = findComparableAppVersion({ appsVersions: apps, app, version: lastPublishedVersion });
  return buildAppDiffs(lastPublishedObject, app);
});
export const activeAppIssues = createSelector('activeAppIssues', [issues, activeApp], (issues, app) => issues?.filter((n) => n?.appId === app?.id));
export const activeAppActionIssues = createSelector('activeAppActionIssues', [activeAppIssues, activeAppAction], (issues, action) => issues?.filter((n) => n?.actionId === action?.id));

const appScreenSelectors = {
  activeApp,
  activeAppId,
  activeAppMetadata,
  activeAppAction,
  activeAppActionAgentSkillTree,
  activeAppActionAgentSkillTreePercentage,

  activeAppDefaultTestCred,
  activeAppAuthTestCreds,
  activeAppLatestTestCred,
  activeAppUseAdminCredentials,

  activeAppActionById,
  activeAppActionArgument,
  activeAppActionArgumentChildren,
  activeAppActionUsesRawBody,
  activeAppActionInstance,
  activeAppActionInstanceById,
  activeAppActionInstanceArguments,
  activeAppActionInstanceArgumentsById,
  activeAppActionInstanceArgumentByKeyAndIndex,
  activeAppActionInstanceOptions,

  activeAppActionLongPollingInstance,
  activeAppExtraActionLongPollingInstance,
  activeAppActionLongPollingInstanceArguments,
  activeAppExtraActionLongPollingInstanceArguments,
  activeAppActionLongPollingInstanceArgumentByKeyAndIndex,
  activeAppExtraActionLongPollingInstanceArgumentByKeyAndIndex,
  activeAppActionLongPollingInstanceOptions,
  activeAppExtraActionLongPollingInstanceOptions,

  activeAppActionFeatures,
  activeAppActionFeaturesCustomArguments,
  activeAppActionFeaturesCustomArgumentsByKeyAndIndex,

  activeAppActionListInstanceOptions,
  activeAppActionListInstanceArguments,
  activeAppActionListInstanceArgumentByKeyAndIndex,
  activeAppActionListTestInstanceOptions,
  activeAppActionListTestInstanceArguments,
  activeAppActionListTestInstanceArgumentByKeyAndIndex,

  activeAppActionFeaturesFilters,
  activeAppActionFeaturesFiltersInstance,
  activeAppActionFeaturesFiltersInstanceOptions,
  activeAppActionFeaturesFiltersInstanceArguments,
  activeAppActionFeaturesFiltersInstanceArgumentByKeyAndIndex,

  activeAppActionOnCompletionHookInstance,
  activeAppActionOnCompletionHookInstanceArguments,
  activeAppActionOnCompletionHookInstanceOptions,
  activeAppActionOnCompletionHookInstanceArgumentByKeyAndIndex,

  activeAppAllAuths,
  activeAppAuth,
  activeAppDefaultAuth,
  activeAppAuthArgument,
  activeAppOAuth2,
  activeAppApi,
  activeAppApis,
  activeAppApiById,
  activeAppApiArgument,
  activeAppTriggerPayload,
  activeAppActionOutputs,
  activeAppActionResponse,
  activeAppAction200Output,
  activeAppAction400Output,
  activeAppAction500Output,
  activeAppActionOutboundResponse,
  activeAppActionAPI,
  activeAppMappers,
  activeAppTriggerMappers,
  activeAppOutboundMappers,
  activeAppActionApiMappers,
  activeAppActionApiTriggerMappers,
  activeAppActionApiOutboundMappers,
  activeAppActionOutboundMappers,
  activeAppActionAttribute,
  actionSignatureGroupValidation,
  actionAttributesGroupValidation,
  actionOutputGroupValidation,
  actionOutboundOutputGroupValidation,
  actionPresentationGroupValidation,
  appDiffs,
  activeAppIssues,
  activeAppActionIssues
};

export default appScreenSelectors;
